import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronDown, Menu, X } from 'lucide-react';
import { siteData } from '../../data/mock';

const HEADER_HEIGHT = 70;

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [selectedLang, setSelectedLang] = useState(siteData.nav.languages[0]);
  const langRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNavClick = useCallback((e, href) => {
    e.preventDefault();
    const id = href.replace('#', '');
    const el = document.getElementById(id);
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - HEADER_HEIGHT;
      window.scrollTo({ top, behavior: 'smooth' });
    }
    setMobileOpen(false);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-[#0a1a3d]/95 backdrop-blur-sm shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-[70px]">
          {/* Logo */}
          <a href="#" className="flex items-center gap-2 no-underline">
            <div className="w-8 h-8 bg-[#0E47B0] rounded-md flex items-center justify-center">
              <span className="text-white font-bold text-sm">L</span>
            </div>
            <span className="text-white font-bold text-xl tracking-tight">Lex Recupero</span>
          </a>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            {siteData.nav.links.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="text-white/80 hover:text-white text-[15px] font-medium no-underline transition-colors"
                data-testid={`nav-${link.href.replace('#', '')}`}
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* Right side */}
          <div className="hidden md:flex items-center gap-4">
            {/* Language selector */}
            <div className="relative" ref={langRef}>
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="flex items-center gap-1.5 text-white/80 hover:text-white text-sm font-medium bg-transparent border-none cursor-pointer transition-colors"
              >
                <span className="text-base">{selectedLang.flag}</span>
                <span>{selectedLang.label}</span>
                <ChevronDown size={14} className={`transition-transform ${langOpen ? 'rotate-180' : ''}`} />
              </button>
              {langOpen && (
                <div className="absolute right-0 top-full mt-2 bg-white rounded-lg shadow-xl py-1 min-w-[160px] overflow-hidden">
                  {siteData.nav.languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => { setSelectedLang(lang); setLangOpen(false); }}
                      className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-2 hover:bg-gray-50 transition-colors border-none cursor-pointer ${
                        selectedLang.code === lang.code ? 'bg-blue-50 text-[#0E47B0] font-medium' : 'text-gray-700 bg-transparent'
                      }`}
                    >
                      <span>{lang.flag}</span>
                      <span>{lang.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Login */}
            <a
              href="#"
              className="text-white/80 hover:text-white text-sm font-medium no-underline transition-colors"
            >
              {siteData.nav.loginText}
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden text-white bg-transparent border-none cursor-pointer p-1"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-[#0a0a14]/98 backdrop-blur-sm border-t border-white/10">
          <div className="px-4 py-4 space-y-3">
            {siteData.nav.links.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className="block text-white/80 hover:text-white text-[15px] font-medium no-underline py-2"
                onClick={(e) => handleNavClick(e, link.href)}
                data-testid={`mobile-nav-${link.href.replace('#', '')}`}
              >
                {link.label}
              </a>
            ))}
            <div className="pt-2 border-t border-white/10">
              <a href="#" className="block text-white/80 hover:text-white text-sm font-medium no-underline py-2">
                {siteData.nav.loginText}
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
